import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  collection, getAggregateFromServer, getCountFromServer, getDocs, limit, orderBy, query, sum, where,
} from "firebase/firestore";
import { db } from "../firebase";
import { PERIODS, periodWindows, toMillis } from "../utils/overviewData";

const daysFor = (periodId) => (PERIODS.find((period) => period.id === periodId) || PERIODS[0]).days;
const DEALERS_IN_FLIGHT = 8;

const count = async (q) => (await getCountFromServer(q)).data().count;

const inRange = (ref, field, from, to) =>
  query(ref, where(field, ">=", new Date(from)), where(field, "<", new Date(to)));

/** One count per chart day, the period's total, and the period before it. */
async function perDay(ref, field, win) {
  const series = await Promise.all(
    win.series.map(async (day) => ({ ...day, count: await count(inRange(ref, field, day.start, day.end)) }))
  );
  const total = (days) => days.reduce((n, day) => n + day.count, 0);
  const previous = win.series.length > win.days
    ? total(series.filter((day) => !day.inPeriod))
    : await count(inRange(ref, field, win.prevStart, win.prevEnd));
  return { series, current: total(series.filter((day) => day.inPeriod)), previous };
}

async function readListings(win) {
  const ref = collection(db, "properties");
  const byStatus = (status) => count(query(ref, where("status", "==", status)));
  const [all, deleted, inactive, pending, added] = await Promise.all([
    count(ref), byStatus("deleted"), byStatus("inactive"), byStatus("pending"), perDay(ref, "createdAt", win),
  ]);
  // Listings without a status are live, as the rules read them, so live is
  // what is left once the other states are counted.
  return {
    total: all - deleted,
    live: all - deleted - inactive - pending,
    hidden: inactive + pending,
    added,
  };
}

async function readSignups(win) {
  const ref = collection(db, "users");
  const [total, dealers, added] = await Promise.all([
    count(ref), count(query(ref, where("role", "==", "dealer"))), perDay(ref, "createdAt", win),
  ]);
  return { total, dealers, tenants: total - dealers, ...added };
}

async function readQueue(win) {
  const reports = collection(db, "reports");
  const [pendingReviews, pendingDeletions, current, previous] = await Promise.all([
    count(query(collection(db, "reviews"), where("status", "==", "pending"))),
    count(query(collection(db, "users"), where("status", "==", "pending_deletion"))),
    count(inRange(reports, "createdAt", win.start, win.end)),
    count(inRange(reports, "createdAt", win.prevStart, win.prevEnd)),
  ]);
  return { pendingReviews, pendingDeletions, reports: { current, previous } };
}

async function readReels(win) {
  const ref = collection(db, "propertyReels");
  const [live, views, current, previous] = await Promise.all([
    count(query(ref, where("status", "==", "active"))),
    getAggregateFromServer(ref, { views: sum("views") }).then((snap) => snap.data().views || 0),
    count(inRange(ref, "createdAt", win.start, win.end)),
    count(inRange(ref, "createdAt", win.prevStart, win.prevEnd)),
  ]);
  return { live, views, published: { current, previous } };
}

async function readPush() {
  const [devices, latest] = await Promise.all([
    count(collection(db, "fcm_tokens")),
    getDocs(query(collection(db, "notifications"), orderBy("sentAt", "desc"), limit(1))),
  ]);
  const broadcast = latest.docs[0]?.data();
  return {
    devices,
    lastBroadcast: broadcast
      ? {
        title: broadcast.title || "Untitled",
        sentAt: toMillis(broadcast.sentAt),
        targetSegment: broadcast.targetSegment || "All Registered Users",
      }
      : null,
  };
}

async function mapLimit(items, size, fn) {
  const results = new Array(items.length);
  let next = 0;
  const worker = async () => {
    while (next < items.length) {
      const i = next++;
      results[i] = await fn(items[i]);
    }
  };
  await Promise.all(Array.from({ length: Math.min(size, items.length) }, worker));
  return results;
}

// Collection-group reads on leads are denied by the rules, so leads are
// counted dealer by dealer: three count queries each. The nearby count is
// shaped to the existing (leadType, createdAt desc) index.
async function readLeads(win) {
  const dealers = await getDocs(query(collection(db, "users"), where("role", "==", "dealer")));
  const rows = await mapLimit(dealers.docs, DEALERS_IN_FLIGHT, async (dealer) => {
    const ref = collection(db, "users", dealer.id, "leads");
    try {
      const [current, previous, nearby] = await Promise.all([
        count(inRange(ref, "createdAt", win.start, win.end)),
        count(inRange(ref, "createdAt", win.prevStart, win.prevEnd)),
        count(query(
          ref,
          where("leadType", "==", "nearby"),
          where("createdAt", ">=", new Date(win.start)),
          where("createdAt", "<", new Date(win.end)),
          orderBy("createdAt", "desc")
        )),
      ]);
      const data = dealer.data();
      return { uid: dealer.id, name: data.name || data.businessName || "Unnamed dealer", current, previous, nearby };
    } catch (err) {
      console.error(`Dashboard lead count failed for dealer ${dealer.id}:`, err);
      return null;
    }
  });

  const counted = rows.filter(Boolean);
  const totals = counted.reduce(
    (acc, row) => ({
      current: acc.current + row.current,
      previous: acc.previous + row.previous,
      nearby: acc.nearby + row.nearby,
    }),
    { current: 0, previous: 0, nearby: 0 }
  );
  const failed = rows.length - counted.length;
  return {
    state: failed ? "partial" : "ready",
    ...totals,
    direct: totals.current - totals.nearby,
    dealerCount: dealers.size,
    failed,
    busiest: counted
      .filter((row) => row.current > 0)
      .map((row) => ({ uid: row.uid, name: row.name, direct: row.current - row.nearby, nearby: row.nearby, total: row.current }))
      .sort((a, b) => b.direct - a.direct || b.nearby - a.nearby)
      .slice(0, 5),
  };
}

const SECTIONS = {
  listings: readListings,
  signups: readSignups,
  queue: readQueue,
  reels: readReels,
  push: readPush,
};

/**
 * Everything the admin dashboard shows, as one-shot counts: on load, on a
 * period change and on refresh. The per-dealer lead counts take longest, so
 * they arrive on their own and the rest of the page does not wait for them.
 */
export function useAdminOverview(periodId) {
  const days = daysFor(periodId);
  const [now, setNow] = useState(() => Date.now());
  const [sections, setSections] = useState({});
  const [errors, setErrors] = useState({});
  const [leads, setLeads] = useState({ state: "loading" });
  const [settled, setSettled] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  const runRef = useRef(0);

  const win = useMemo(() => periodWindows(days, now), [days, now]);

  useEffect(() => {
    const run = ++runRef.current;
    const isCurrent = () => run === runRef.current;
    const keys = Object.keys(SECTIONS);
    setRefreshing(true);

    Promise.allSettled(keys.map((key) => SECTIONS[key](win))).then((results) => {
      if (!isCurrent()) return;
      const next = {};
      const failed = {};
      results.forEach((result, i) => {
        if (result.status === "fulfilled") {
          next[keys[i]] = result.value;
        } else {
          console.error(`Dashboard ${keys[i]} read failed:`, result.reason);
          next[keys[i]] = null;
          failed[keys[i]] = true;
        }
      });
      setSections(next);
      setErrors((prev) => ({ ...failed, leads: prev.leads }));
      setSettled(true);
      setRefreshing(false);
      setUpdatedAt(Date.now());
    });

    readLeads(win)
      .then((result) => {
        if (!isCurrent()) return;
        setLeads(result);
        setErrors((prev) => ({ ...prev, leads: false }));
      })
      .catch((err) => {
        if (!isCurrent()) return;
        console.error("Dashboard dealer list read failed:", err);
        setLeads({ state: "error" });
        setErrors((prev) => ({ ...prev, leads: true }));
      });

    return () => {
      runRef.current += 1;
    };
  }, [win]);

  const refresh = useCallback(() => setNow(Date.now()), []);

  return {
    status: settled ? "ready" : "loading",
    refreshing: refreshing && settled,
    updatedAt,
    refresh,
    win,
    listings: sections.listings ?? null,
    signups: sections.signups ?? null,
    queue: sections.queue ?? null,
    reels: sections.reels ?? null,
    push: sections.push ?? null,
    leads,
    errors,
  };
}
